import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Shield, Compass, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !password.trim()) {
      toast.error("Please enter email and password");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/admin/login", { email, password });
      localStorage.setItem("adminUser", JSON.stringify(response.data));
      if (response.data.access_token) {
        localStorage.setItem("admin_token", response.data.access_token);
      }
      toast.success("Welcome to Admin Panel");
      navigate("/admin/dashboard", { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string" ? detail : "Login failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#070B14" }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-[#070B14]" />
          </div>
          <h1 className="font-display text-3xl text-white">Admin Portal</h1>
          <p className="text-white/60 font-body mt-2">SoulGuide Administration</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="glass-card p-8 space-y-6">
          <div className="space-y-2">
            <label className="block text-sm text-white/70 font-body">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@soulguide.app"
              className="custom-input"
              data-testid="admin-email"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm text-white/70 font-body">Password</label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="custom-input pr-12"
                data-testid="admin-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="btn-gold w-full py-6 text-lg"
            data-testid="admin-login-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Signing in...
              </>
            ) : (
              <>
                <Shield className="w-5 h-5 mr-2" />
                Sign In
              </>
            )}
          </Button>
        </form>

        {/* Back to App */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate("/")}
            className="text-white/50 hover:text-white font-body text-sm flex items-center justify-center gap-2"
          >
            <Compass className="w-4 h-4" />
            Back to SoulGuide
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
